import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { setTokenProvider, setUnauthenticatedHandler } from "../../services/api/client";
import { cognitoAuthAdapter } from "../../services/auth/cognitoAdapter";
import { mockAuthAdapter } from "../../services/auth/mockAdapter";
import type { AuthPort, Session } from "../../services/auth/port";
import { forgetModel } from "../chat/lastModel";
import { AuthContext, type AuthContextValue, type AuthStatus } from "./context";

/**
 * Owns the session and wires the API client's token provider.
 *
 * `status` exists so guards can distinguish "signed out" from "we don't know
 * yet". Collapsing those is the single most common SPA auth bug: every reload
 * flashes the login screen. It matters more here than usual, because the Stripe
 * round-trip is a deliberate full page leave — the user hits this boot path
 * immediately after paying you.
 *
 * ⚠️ THIS IS THE SINGLE SWAP POINT. A test asserts nothing else in the app
 * imports mockAdapter, so the choice cannot be made anywhere but here.
 *
 * The selector is `VITE_MOCKS`, the same flag that drives the MSW network seam,
 * so auth and the API can never disagree about which world they are in — a
 * mocked API with real Cognito (or the reverse) is a confusing half-state that
 * this makes unrepresentable.
 *
 * The mock branch cannot be SELECTED in production: the build refuses to compile
 * with VITE_MOCKS=on (vite.config.ts), so this ternary constant-folds to Cognito.
 *
 * That is not the same as the mock adapter not shipping, and this docstring used
 * to conflate the two. The import below is static, so until 2026-08-25 the
 * adapter and the mock database travelled inside the eager production chunk —
 * unreachable, but downloaded by every visitor and greppable in dist. The
 * `noMockAuthInProduction` plugin now replaces this module with a throwing stub
 * in a production build and then asserts the markers are absent from the eager
 * graph, so the stronger claim is now true and, more importantly, checked.
 */
const adapter: AuthPort =
  import.meta.env.VITE_MOCKS === "on" ? mockAuthAdapter : cognitoAuthAdapter;

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>("loading");
  const [session, setSession] = useState<Session | null>(null);
  /**
   * The cache is part of the session, and dropping one without the other is how
   * a signed-out tab keeps someone's email on screen.
   *
   * AuthProvider is mounted INSIDE QueryClientProvider (main.tsx), so this hook
   * resolves. Every place below that ends a session clears it — see the two
   * call sites for what each one is actually protecting against.
   */
  const queryClient = useQueryClient();
  /**
   * Set when the SERVER has confirmed an 18+ declaration in this session.
   *
   * `dobVerified` normally arrives as an ID-token claim, which means it is only
   * true once a token minted AFTER the write reaches this tab. Everything in
   * that sentence can fail independently — the refresh can be rejected, the
   * refresh token can be missing (sessionStorage is per-tab), the claim can lag
   * — and every one of those failures looks the same to the user: they answer
   * the gate and the guard sends them back to it. A loop with no error, which is
   * exactly what this gate has already done once.
   *
   * So a confirmed 200 from POST /me/age is treated as authoritative for the
   * current session. It is not a weakening of the gate: the server did the
   * check, wrote the attribute and the audit row, and this only reflects what it
   * already recorded. Held in memory ONLY — a reload re-reads the claim, so this
   * can never become a client-held flag that outlives the truth.
   */
  const [declaredAdult, setDeclaredAdult] = useState(false);

  useEffect(() => {
    /*
     * No "have I already booted?" ref here, deliberately.
     *
     * An earlier version guarded this with one, to stop StrictMode's double
     * invoke from calling restoreSession twice. That deadlocks: StrictMode runs
     * effect → cleanup → effect. The cleanup sets the first run's `cancelled`
     * flag, and the second run hits the ref and returns early — so nothing ever
     * resolves and `status` stays "loading" forever. Every guarded route then
     * renders its skeleton for good.
     *
     * The correct shape is the standard one: let the effect run twice and make
     * it idempotent. restoreSession() is a read, so a duplicate call in dev is
     * harmless; the `cancelled` flag discards the stale result.
     */
    let cancelled = false;

    setTokenProvider(() => adapter.getAccessToken());
    setUnauthenticatedHandler(() => {
      setSession(null);
      setStatus("anonymous");
      /* A 401 means the token this cache was filled through is no longer good
         for anything. Keeping the data it fetched would leave the last account's
         numbers rendering under a session the server has already refused. */
      queryClient.clear();
    });

    /*
     * BOOT CANNOT BE ALLOWED TO HANG, and a .then/.catch pair does not promise
     * that — it promises we handle both outcomes IF one arrives. A promise that
     * never settles has neither, and this one already did exactly that: the
     * token refresh inside restoreSession() called fetch with no timeout, so a
     * hung request left `status` on "loading" for the life of the tab and every
     * guarded route rendered LOADING… with nothing to click.
     *
     * The real fix is the timeout in cognitoAdapter (TOKEN_TIMEOUT_MS). This is
     * the backstop, because the failure mode is far worse than its cause is
     * rare: any future await added to this path — a feature flag fetch, a config
     * load — reintroduces a dead app unless whoever adds it remembers. Fifteen
     * seconds sits deliberately above the adapter's ten, so in the ordinary case
     * the adapter reports the failure and this never fires.
     *
     * Falling to "anonymous" is the right landing: it shows a sign-in page,
     * which someone can act on. Staying on "loading" shows a spinner, which
     * nobody can.
     */
    const bootTimer = setTimeout(() => {
      if (!cancelled) {
        console.warn("auth boot did not settle in 15s, treating as signed out");
        setStatus((s) => (s === "loading" ? "anonymous" : s));
      }
    }, 15_000);

    adapter
      .restoreSession()
      .then((s) => {
        if (cancelled) return;
        clearTimeout(bootTimer);
        setSession(s);
        setStatus(s ? "authenticated" : "anonymous");
      })
      .catch(() => {
        /* Fail closed: if we can't establish a session, we don't have one. */
        if (!cancelled) {
          clearTimeout(bootTimer);
          setStatus("anonymous");
        }
      });

    const unsub = adapter.onSessionChange((s) => {
      setSession(s);
      setStatus(s ? "authenticated" : "anonymous");
    });

    return () => {
      cancelled = true;
      clearTimeout(bootTimer);
      unsub();
      setTokenProvider(null);
      setUnauthenticatedHandler(null);
    };
    // queryClient is referentially stable for the life of the provider, so
    // naming it here does not re-run boot — it just keeps the list truthful.
  }, [queryClient]);

  const value = useMemo<AuthContextValue>(
    () => ({
      status,
      // Merged HERE, at the one boundary every consumer reads, so the override
      // survives a refreshSession() that replaces the session object — and so
      // there is exactly one place where "is this user 18+" is answered.
      session: session && declaredAdult ? { ...session, dobVerified: true } : session,
      beginSignIn: (opts) => adapter.beginSignIn(opts),
      completeSignIn: async () => {
        const r = await adapter.completeSignIn();
        setSession(r.session);
        setStatus("authenticated");
        return r;
      },
      logout: async () => {
        await adapter.logout();
        setSession(null);
        setStatus("anonymous");
        /*
         * UNCONDITIONALLY, and before anyone points out that production already
         * gets this for free.
         *
         * It does, by accident: cognitoAdapter.logout ends in a
         * window.location.assign to Cognito, and a full navigation destroys the
         * heap and the cache with it. But that line sits inside
         * `if (DOMAIN && CLIENT_ID)`, it never runs under mocks, and it is
         * asynchronous — so Account's "Switch account" button, which awaits this
         * and then navigates in-app, leaves a live window where the app is
         * signed out, still mounted, and still holding the previous person's
         * profile. That is the one flow on the one page now showing a balance.
         *
         * clear() rather than removing named keys: chat history, conversations
         * and coverage are all one account's too, and a list of keys to forget
         * is a list someone has to remember to add to.
         */
        queryClient.clear();

        /* The remembered chat model lives in localStorage, which queryClient
           does not own and clear() therefore does not touch. It is named
           explicitly despite the rule just above, because the localStorage
           equivalent of clear() would also destroy the mock database the dev
           and test builds run on.

           Low stakes by construction — `pickChatModel` re-checks the id against
           the NEW session's entitlements, so a leftover value can only ever
           choose among models the next reader could already open. It is dropped
           anyway: on a shared device, routing the next person into the previous
           one's model is a small betrayal of what sign-out means. */
        forgetModel();
      },
      markAdultDeclared: () => setDeclaredAdult(true),
      refreshSession: async () => {
        const s = await adapter.refreshSession();
        // Set state here rather than leaning on the onSessionChange listener:
        // the caller awaits this and then navigates, and a guard reading a
        // context value that has not re-rendered yet is the same redirect loop
        // this method exists to end.
        setSession(s);
        setStatus(s ? "authenticated" : "anonymous");
        return s;
      },
      resendVerification: (email) => adapter.resendVerification(email),
    }),
    // declaredAdult belongs here: it changes what `session` reports, so leaving
    // it out would hand consumers a memoised value that still says false.
    // queryClient is referentially stable for the life of the provider, so it
    // costs nothing here and keeps the dependency list honest.
    [status, session, declaredAdult, queryClient],
  );

  return <AuthContext value={value}>{children}</AuthContext>;
}
